import {
  HttpErrorResponse,
  HttpInterceptorFn,
  HttpRequest,
  HttpEvent,
  HttpHandlerFn,
} from '@angular/common/http'
import { Observable, throwError, catchError, timer, switchMap } from 'rxjs'
import { environment } from 'src/environments/environment'

const DEBUG = environment.logLevel === 'debug'

// Header to mark requests that have already been retried with fallback URL
const FALLBACK_ATTEMPTED_HEADER = 'X-Fallback-Attempted'

// LocalStorage key for tracking which URL is working
const FALLBACK_STATUS_KEY = 'h2h_api_fallback_status'
const FALLBACK_STATUS_EXPIRY_KEY = 'h2h_api_fallback_status_expiry'

// How long to remember the fallback status (5 minutes)
const FALLBACK_STATUS_TTL = 5 * 60 * 1000

/**
 * Check if error indicates DNS resolution failure
 */
function isDnsResolutionError(error: HttpErrorResponse): boolean {
  // Status 0 indicates network error (could be DNS, CORS, or connection failure)
  if (error.status === 0) {
    // Check if it's specifically a DNS error by examining the error message
    const errorMessage = error.message?.toLowerCase() || ''
    const isDnsError =
      errorMessage.includes('dns') ||
      errorMessage.includes('enotfound') ||
      errorMessage.includes('name resolution failed') ||
      errorMessage.includes('could not resolve host') ||
      errorMessage.includes('getaddrinfo') ||
      // Generic network errors that might be DNS-related
      errorMessage.includes('failed to fetch') ||
      errorMessage.includes('network request failed')

    return isDnsError
  }

  return false
}

/**
 * Get the current fallback status from localStorage
 */
function getFallbackStatus(): 'primary' | 'fallback' | null {
  try {
    const expiry = localStorage.getItem(FALLBACK_STATUS_EXPIRY_KEY)
    if (expiry && Date.now() > parseInt(expiry)) {
      // Status has expired, clear it
      localStorage.removeItem(FALLBACK_STATUS_KEY)
      localStorage.removeItem(FALLBACK_STATUS_EXPIRY_KEY)
      return null
    }
    return localStorage.getItem(FALLBACK_STATUS_KEY) as
      | 'primary'
      | 'fallback'
      | null
  } catch (e) {
    return null
  }
}

/**
 * Set the fallback status in localStorage
 */
function setFallbackStatus(status: 'primary' | 'fallback') {
  try {
    localStorage.setItem(FALLBACK_STATUS_KEY, status)
    localStorage.setItem(
      FALLBACK_STATUS_EXPIRY_KEY,
      (Date.now() + FALLBACK_STATUS_TTL).toString(),
    )
  } catch (e) {
    // Ignore localStorage errors
  }
}

/**
 * Transform a request URL from primary to fallback or vice versa
 */
function transformUrl(url: string, useFallback: boolean): string {
  if (!environment.apiFallbackUrl) {
    return url
  }

  const primaryHost = new URL(environment.apiBaseUrl).host
  const fallbackHost = new URL(environment.apiFallbackUrl).host

  if (useFallback) {
    // Replace primary host with fallback host
    return url.replace(primaryHost, fallbackHost)
  } else {
    // Replace fallback host with primary host
    return url.replace(fallbackHost, primaryHost)
  }
}

/**
 * Check if a URL is using the primary API host
 */
function isPrimaryUrl(url: string): boolean {
  try {
    const primaryHost = new URL(environment.apiBaseUrl).host
    return url.includes(primaryHost)
  } catch (e) {
    return false
  }
}

/**
 * DNS Fallback Interceptor
 *
 * This interceptor detects DNS resolution failures when accessing the primary API URL
 * (api.home-home.org via Cloudflare) and automatically retries the request using the
 * direct CloudFront distribution URL as a fallback.
 *
 * SSL certificates: The CloudFront *.cloudfront.net domain has a valid Amazon SSL
 * certificate, so HTTPS requests will work without certificate errors.
 */
export const dnsFallbackInterceptor: HttpInterceptorFn = (
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> => {
  // Only apply to API requests
  if (
    !req.url.includes(environment.apiBaseUrl) &&
    !(
      environment.apiFallbackUrl && req.url.includes(environment.apiFallbackUrl)
    )
  ) {
    return next(req)
  }

  // Skip if this request has already attempted fallback
  if (req.headers.has(FALLBACK_ATTEMPTED_HEADER)) {
    return next(req)
  }

  // Check if admin has forced fallback DNS for testing
  let forceFallback = false
  try {
    forceFallback = localStorage.getItem('force_fallback_dns') === 'true'
  } catch (e) {
    // Ignore localStorage errors
  }
  if (forceFallback && isPrimaryUrl(req.url) && environment.apiFallbackUrl) {
    const fallbackReq = req.clone({
      url: transformUrl(req.url, true),
      headers: req.headers.set(FALLBACK_ATTEMPTED_HEADER, 'forced'),
    })
    return next(fallbackReq)
  }

  // Check if we should proactively use the fallback URL based on recent history
  const fallbackStatus = getFallbackStatus()
  if (
    fallbackStatus === 'fallback' &&
    isPrimaryUrl(req.url) &&
    environment.apiFallbackUrl
  ) {
    if (DEBUG) {
      console.log(
        `🔄 [DNS-Fallback] Proactively using fallback URL based on recent failures: ${req.url}`,
      )
    }
    // Modify the request to use fallback URL from the start
    const fallbackReq = req.clone({
      url: transformUrl(req.url, true),
      headers: req.headers.set(FALLBACK_ATTEMPTED_HEADER, 'proactive'),
    })
    return next(fallbackReq)
  }

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      // Only attempt fallback for DNS-related errors
      if (!isDnsResolutionError(error)) {
        return throwError(() => error)
      }

      // Don't retry if we don't have a fallback URL configured
      if (!environment.apiFallbackUrl) {
        return throwError(() => error)
      }

      // Only retry if we're using the primary URL (don't retry fallback failures)
      if (!isPrimaryUrl(req.url)) {
        return throwError(() => error)
      }

      // Remember that primary is failing
      setFallbackStatus('fallback')

      // Transform URL to use fallback
      const fallbackUrl = transformUrl(req.url, true)

      // Clone the request with the fallback URL
      const fallbackReq = req.clone({
        url: fallbackUrl,
        headers: req.headers.set(FALLBACK_ATTEMPTED_HEADER, 'true'),
      })

      // Add a small delay before retrying to avoid hammering the server
      return timer(500).pipe(
        switchMap(() => next(fallbackReq)),
        catchError((fallbackError: HttpErrorResponse) => {
          return throwError(() => fallbackError)
        }),
      )
    }),
  )
}
